import { Link } from "react-router-dom";
import { ForgotPassword1AlertProps, ForgotPassword1Fields } from "../../components/SmartErrorAlert";
import { hasErrorMessage, hasInvalidField } from "../getApiErrorMessage";
import translate from "../translate";
import { ErrorContentGenerator, ErrorContentMap, InvalidField, InvalidFieldMessages } from "./getAlertContent";

type ForgotPassword1ErrState =
    'notFound' |
    'deactivatedAccount' |
    'locked' |
    'unknown' |
    'tempPassword' |
    'needTempPassword' |
    'tempUsername';

export const apiErrorContent: ErrorContentMap<ForgotPassword1ErrState> = {
    notFound: {
        header: translate("we_cant_find_you"),
        body: <>
            {translate("check_information_try_again")}{' '}
            {translate("for_more_help_call")} (<a href="tel:18006334227">1-800-633-4227</a>).{" "}
            {translate("tty_users_can_call")}{" "}<a href="tel:18774862048">1-877-486-2048</a>.
        </>,
        analyticsErr: "We can't find you in our records. Check the information you entered and try again. For more help, call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048."
    },
    deactivatedAccount: {
        header: translate('your_account_not_active'),
        body: <>
            {translate('to_reactivate_account_call_medicare')} ({<a href="tel:18006334227">1-800-633-4227</a>}).
            {' '}{translate('tty_users_can_call')} {<a href="tel:1-877-486-2048">1-877-486-2048</a>}.
        </>,
        analyticsErr: 'Your account is no longer active. To reactivate your account, call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048.',
    },
    locked: {
        header: translate('your_account_now_locked_protect_information'),
        body: <Link to="/unlock-account">{translate('unlock_my_account')}.</Link>,
        analyticsErr: 'Your account is now locked to protect your information. Unlock my account.',
    },
    tempPassword: {
        headerJSX: <>
            {translate('you_must')}
            {' '}<Link to="/login">{translate('log_in')}</Link>
            {' '}{translate('with_temporary_password_you_can_reset_password')}
        </>,
        body: <>
            {translate("if_unable_to_login_call_medicare")} (<a href="tel:18006334227">1-800-633-4227</a>).
            {" "}{translate("tty_users_can_call")}{" "}<a href="tel:18774862048">1-877-486-2048</a>.
        </>,
        analyticsErr: "You must log in with your temporary password before you can reset your password. If you're unable to log in with your temporary password, call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048."
    },
    needTempPassword: {
        header: translate("we_cant_access_secret_question_answer"),
        body: <>
            {translate("please_call_at")} (<a href="tel:18006334227">1-800-633-4227</a>) {''}
            {translate("to_get_temp_password_log_into_account")} {' '}
            {translate("tty_users_can_call")}{" "}<a href="tel:18774862048">1-877-486-2048</a>.
        </>,
        analyticsErr: "We can't access your secret question and answer. Please call 1-800-MEDICARE (1-800-633-4227) to get a temporary password to log into your account. TTY users can call 1-877-486-2048."
    },
    tempUsername: {
        headerJSX: <>
            {translate('you_must')}
            {' '}<Link to="/login">{translate('log_in')}</Link>
            {' '}{translate('with_temporary_username_reset_password')}
        </>,
        body: <>
            {translate("if_unable_to_login_temporary_username")} (<a href="tel:18006334227">1-800-633-4227</a>).
            {" "}{translate("tty_users_can_call")}{" "}<a href="tel:18774862048">1-877-486-2048</a>.
        </>,
        analyticsErr: "You must log in with your temporary username before you can reset your password. If you're unable to log in with your temporary username, call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048."
    },
    unknown: {
        header: translate("we_cant_process_request"),
        body: translate("we_are_sorry_for_inconvenience_try_resetting_password_later"),
        analyticsErr: "We can't process your request at this time. We're sorry for any inconvenience. Try resetting your password later."
    }
}

const invalidFieldMessages: InvalidFieldMessages<ForgotPassword1Fields> = {
    username: {
        linkText: 'username',
        messages: {
            empty: 'enter_your_usermame_with_period',
        }
    },
    mbi: {
        linkText: 'medicare_number',
        messages: {
            empty: 'enter_your_medicare_number',
            invalid: 'check_mbi'
        }
    },
    lastName: {
        linkText: 'last_name',
        messages: {
            empty: 'enter_your_last_name',
        }
    },
    dateOfBirth: {
        linkText: 'date_of_birth',
        messages: {
            empty: 'enter_your_dob',
        }
    }
}

export const forgotPasswordStep1ErrorFns = new ErrorContentGenerator<ForgotPassword1AlertProps, ForgotPassword1Fields, ForgotPassword1ErrState>({
    apiErrorContent,
    invalidFieldMessages,
    invalidFieldHeader: 'complete_items_to_reset_password',
    getRefMap: props => {
        return {
            mbi: props.mbiRef,
            username: props.usernameRef,
            lastName: props.lastNameRef,
            dateOfBirth: props.dateOfBirthRef
        }
    },
    getErrState: apiResponse => {
        if (!apiResponse) {
            return null;
        }
        if (!apiResponse.code) {
            return 'unknown';
        }
        switch (apiResponse.code) {
            case 400:
                const invalidFields = forgotPasswordStep1ErrorFns.getApiInvalidFields(apiResponse);
                if (invalidFields.length) {
                    return null;
                }
                const { errors } = apiResponse;
                if (!errors) {
                    return 'unknown';
                }
                const isMissingQuestions = hasErrorMessage(errors, 'missing questions');
                const isTempPassword = hasErrorMessage(errors, 'temporary password');
                const isTempUsername = hasErrorMessage(errors, 'temporary username');
                if (isTempPassword) {
                    return 'tempPassword';
                } else if (isMissingQuestions) {
                    return 'needTempPassword';
                }
                if (isTempUsername) {
                    return 'tempUsername';
                }
                if (hasErrorMessage(errors, 'did not match')) {
                    return 'notFound';
                }
                if (hasErrorMessage(errors, 'user is disabled')) {
                    return 'deactivatedAccount';
                }
                if (hasErrorMessage(errors, 'locked')) {
                    return 'locked';
                }
                return 'unknown';
            case 404:
                return 'notFound';
            default:
                return 'unknown';
        }
    },
    getApiInvalidFields: apiResponse => {
        if (!apiResponse || apiResponse.code !== 400) {
            return [];
        }
        const invalidFields: InvalidField<ForgotPassword1Fields>[] = [];
        const { errors } = apiResponse;
        const invalidMbi = hasInvalidField(errors, 'mbi');
        if (invalidMbi) {
            invalidFields.push({ name: 'mbi', type: 'invalid' });
        }
        const invalidUsername = hasInvalidField(errors, 'username');
        if (invalidUsername) {
            invalidFields.push({ name: 'username', type: 'invalid' });
        }
        const invalidLastName = hasInvalidField(errors, 'lastName');
        if (invalidLastName) {
            invalidFields.push({ name: 'lastName', type: 'invalid' });
        }
        const invalidDateOfBirth = hasInvalidField(errors, 'dateOfBirth');
        if (invalidDateOfBirth) {
            invalidFields.push({ name: 'dateOfBirth', type: 'invalid' });
        }

        return invalidFields;
    },
    isIntermediateStep: true
});